import { useEffect, useRef, useState } from "react"
import { Search } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/Popover"
import { openProductQuickView } from "@/components/productQuickView/store"
import { products_search_path } from "@/lib/routes"

type ProductHit = { external_id: string; short_name: string | null; name: string }
// Results remember the term they answer. Older answers are shown dimmed while a
// newer term loads, never as the answer to it.
type Results = { term: string; products: ProductHit[]; failed: boolean }

// Fills the top bar between the market block and the bell. Lists up to 10 of
// the org's products by name, short name, SKU or id. Picking one opens the
// product quick view.
export default function ProductSearch() {
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)
  const [results, setResults] = useState<Results>({ term: "", products: [], failed: false })
  const term = query.trim()

  useEffect(() => {
    if (term.length < 2) return
    const controller = new AbortController()
    const timer = setTimeout(() => {
      fetch(products_search_path({ q: term }), { headers: { Accept: "application/json" }, signal: controller.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((products: ProductHit[]) => setResults({ term, products, failed: false }))
        .catch((e) => { if (e.name !== "AbortError") setResults({ term, products: [], failed: true }) })
    }, 200)
    return () => { clearTimeout(timer); controller.abort() }
  }, [term])

  const stale = results.term !== term
  const showList = open && term.length >= 2 && results.term !== ""

  // The list renders through a portal at the end of <body>, so sticky grid
  // headers and fixed page panels never cover it.
  return (
    <Popover open={showList}>
      <PopoverAnchor asChild>
        {/* A white field on the navy bar. */}
        <div className="relative flex h-full flex-1 items-center border-r border-top-bar-border px-3">
          <Search size={15} className="pointer-events-none absolute left-6 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={(e) => { if (e.key === "Escape") e.currentTarget.blur() }}
            placeholder="Search by id, short name or name"
            aria-label="Search products"
            className="h-8 w-full rounded-md bg-background pl-9 pr-3 text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        sideOffset={0}
        // Typing stays in the search box: the list never takes focus.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className={`w-[480px] rounded-t-none border-t-0 bg-background p-0 py-1${stale ? " opacity-60" : ""}`}
      >
        {results.failed ? (
          <div className="px-4 py-2 text-[13px] text-foreground">Search failed. Try again.</div>
        ) : results.products.length === 0 ? (
          <div className="px-4 py-2 text-[13px] text-foreground">No products match "{results.term}"</div>
        ) : (
          results.products.map((p) => (
            <Button
              key={p.external_id}
              type="button"
              variant="ghost"
              // Keeps focus in the search box, so its blur does not close the
              // list before the click lands.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                openProductQuickView(p.external_id)
                inputRef.current?.blur()
              }}
              className="h-auto w-full justify-start gap-3 rounded-none px-4 py-2 text-[13px] font-normal text-foreground hover:bg-muted"
            >
              {/* The id column is fixed so every row's text starts at the same
                  place; the short name, when there is one, leads the full name. */}
              <span className="w-11 flex-shrink-0 text-left tabular-nums">{p.external_id}</span>
              <span className="truncate text-left">
                {p.short_name && <span className="mr-2 font-medium">{p.short_name}</span>}
                {p.name}
              </span>
            </Button>
          ))
        )}
      </PopoverContent>
    </Popover>
  )
}
